import { createClient } from '@supabase/supabase-js'
import useAuthStore from '../store/authStore'

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      // Disabilita navigator.locks cross-tab: evita deadlock dove il lock rimane
      // acquisito dopo una navigazione/crash e blocca tutte le chiamate successive.
      // Sicuro per una PWA single-tab.
      lock: async (_name, _acquireTimeout, fn) => fn(),
    }
  }
)

// supabase.auth.getSession() può bloccarsi indefinitamente se il client va in uno stato
// "stuck" (tipicamente dopo che il tab resta aperto a lungo o torna dal background).
// Questa versione fa race con un timeout di 2s e, in caso di fallimento, ripiega sulla
// sessione cachata in authStore. Aggiorna anche la sessione nello store se è stata
// refreshata lato Supabase.
// Usa questa SEMPRE al posto di supabase.auth.getSession() per ottenere un token da
// usare in chiamate edge function / DB / API di terze parti.
//
// Se il token è scaduto (o sta per scadere entro 60s), forza un refresh attivo
// via refreshSession. Senza questo, dopo un tab/dispositivo in sleep il token
// resta nella cache scaduto e le chiamate falliscono con 401 JWT expired.
const REFRESH_LEEWAY_SEC = 60

function isSessionExpired(s) {
  if (!s?.expires_at) return false
  return (s.expires_at - REFRESH_LEEWAY_SEC) <= Math.floor(Date.now() / 1000)
}

async function raceWithTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timeout`)), ms)),
  ])
}

export async function getSessionSafe() {
  const storeSession = useAuthStore.getState().session

  // 1) Se il token cachato è scaduto o quasi, forza un refresh attivo
  if (isSessionExpired(storeSession)) {
    try {
      const result = await raceWithTimeout(supabase.auth.refreshSession(), 4000, 'refreshSession')
      const refreshed = result?.data?.session
      if (refreshed?.access_token) {
        useAuthStore.setState({ session: refreshed })
        return refreshed
      }
    } catch (err) {
      console.warn('[getSessionSafe] refreshSession failed:', err?.message)
      // cadiamo sotto: proviamo comunque getSession come last-resort
    }
  }

  // 2) Path normale: getSession con timeout
  try {
    const result = await raceWithTimeout(supabase.auth.getSession(), 2000, 'getSession')
    const fresh = result?.data?.session
    if (fresh?.access_token && fresh.access_token !== storeSession?.access_token) {
      useAuthStore.setState({ session: fresh })
    }
    return fresh || storeSession
  } catch {
    // getSession hung (client state stuck) → use cached session
    return storeSession
  }
}
